import { useMemo } from 'react'
import { Server, Users, Activity, Globe } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { NodeHealthGauge } from '@/components/charts/NodeHealthGauge'
import { NodeStatusChart } from '@/components/charts/NodeStatusChart'
import { IpPoolDonut } from '@/components/charts/IpPoolDonut'
import { AlertCard } from '@/components/alerts/AlertCard'
import { useDashboard } from '@/hooks/useDashboard'
import { useIpPool } from '@/hooks/useIpPool'
import { formatBytes } from '@/lib/utils'
import type { NodeStatus } from '@/api/types'

export function Dashboard() {
  const { data, isLoading, error } = useDashboard()
  const { data: ipPoolData } = useIpPool()

  const nodeStatusCounts = useMemo(() => {
    const counts: Record<NodeStatus, number> = {
      Active: 0,
      Pending: 0,
      Blocked: 0,
      Rotating: 0,
      Offline: 0,
    }
    data?.nodes.forEach((node) => {
      counts[node.status]++
    })
    return counts
  }, [data?.nodes])

  const avgHealthScore = useMemo(() => {
    if (!data?.nodes.length) return null
    return data.nodes.reduce((sum, node) => sum + node.health_score, 0) / data.nodes.length
  }, [data?.nodes])

  const alerts = data?.recent_alerts ?? []
  const activeAlerts = alerts.filter((a) => !a.acknowledged)

  if (error) {
    return (
      <div className="p-6">
        <Header title="Dashboard" alerts={[]} />
        <div className="mt-6 rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-destructive">Failed to load dashboard: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Header
        title="Dashboard"
        description="Infrastructure overview"
        alerts={alerts}
      />

      <div className="p-6 space-y-6">
        {/* Active Alerts Banner */}
        {activeAlerts.length > 0 && (
          <div className="space-y-2">
            {activeAlerts.slice(0, 2).map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        )}

        {/* Quick Stats Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Server className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Nodes</p>
                <p className="text-2xl font-bold">{isLoading ? '-' : data?.nodes.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                <Users className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold">{isLoading ? '-' : data?.total_users}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-chart-2/10">
                <Activity className="h-6 w-6" style={{ color: 'hsl(221.2 83.2% 53.3%)' }} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Traffic Today</p>
                <p className="text-2xl font-bold">
                  {isLoading ? '-' : formatBytes(data?.total_traffic_today ?? 0)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-warning/10">
                <Globe className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IP Pool</p>
                <p className="text-2xl font-bold">{ipPoolData?.summary.total ?? '-'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Charts Row */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Rotations - Takes 2 columns */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-medium">Recent IP Rotations</CardTitle>
              <Badge variant="outline">{data?.recent_rotations.length ?? 0} in 24h</Badge>
            </CardHeader>
            <CardContent>
              {data?.recent_rotations && data.recent_rotations.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {data.recent_rotations.slice(0, 10).map((rotation, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg border text-sm">
                      <div className="flex items-center gap-3">
                        <div className={`h-2 w-2 rounded-full ${rotation.success ? 'bg-success' : 'bg-destructive'}`} />
                        <div>
                          <span className="font-medium">{rotation.node_id.slice(0, 8)}...</span>
                          <span className="text-muted-foreground ml-2">{rotation.reason}</span>
                        </div>
                      </div>
                      <div className="text-right text-muted-foreground">
                        <div className="text-xs">{rotation.old_ip} → {rotation.new_ip}</div>
                        <div className="text-xs">{new Date(rotation.timestamp).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                  No rotations in the last 24 hours
                </div>
              )}
            </CardContent>
          </Card>

          {/* Health Gauge */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">System Health</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center pt-4">
              {avgHealthScore !== null ? (
                <>
                  <NodeHealthGauge score={avgHealthScore} size={160} />
                  <p className="mt-4 text-sm text-muted-foreground">Average across all nodes</p>
                </>
              ) : (
                <div className="flex h-[160px] items-center justify-center">
                  <p className="text-2xl text-muted-foreground">N/A</p>
                </div>
              )}
              <div className="mt-4 flex gap-2">
                {data?.blocked_nodes && data.blocked_nodes.length > 0 && (
                  <Badge variant="destructive">{data.blocked_nodes.length} blocked</Badge>
                )}
                {nodeStatusCounts.Rotating > 0 && (
                  <Badge variant="secondary">{nodeStatusCounts.Rotating} rotating</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Charts Row */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Node Status Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Node Status</CardTitle>
            </CardHeader>
            <CardContent>
              <NodeStatusChart data={nodeStatusCounts} />
            </CardContent>
          </Card>

          {/* IP Pool Distribution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">IP Pool Status</CardTitle>
            </CardHeader>
            <CardContent>
              {ipPoolData?.summary ? (
                <IpPoolDonut summary={ipPoolData.summary} />
              ) : (
                <div className="flex h-[200px] items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rotation Stats Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">Rotation Stats (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              {data?.recent_rotations ? (
                <div className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Successful</span>
                    <span className="font-bold text-success">
                      {data.recent_rotations.filter(r => r.success).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Failed</span>
                    <span className="font-bold text-destructive">
                      {data.recent_rotations.filter(r => !r.success).length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Success Rate</span>
                    <span className="font-bold">
                      {data.recent_rotations.length > 0
                        ? `${((data.recent_rotations.filter(r => r.success).length / data.recent_rotations.length) * 100).toFixed(0)}%`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex h-[120px] items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Nodes Grid */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-medium">Nodes Overview</CardTitle>
            <Badge variant="outline">{data?.nodes.length ?? 0} total</Badge>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-muted-foreground">Loading...</div>
            ) : data?.nodes.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">No nodes configured</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data?.nodes.map((node) => (
                  <div
                    key={node.id}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-2 w-2 rounded-full ${
                          node.status === 'Active'
                            ? 'bg-success'
                            : node.status === 'Blocked'
                              ? 'bg-destructive'
                              : node.status === 'Rotating'
                                ? 'bg-primary animate-pulse'
                                : 'bg-muted-foreground'
                        }`}
                      />
                      <div>
                        <p className="font-medium text-sm">{node.name}</p>
                        <p className="text-xs text-muted-foreground">{node.region}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{(node.health_score * 100).toFixed(0)}%</p>
                      <p className="text-xs text-muted-foreground">{node.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
